import React, {useEffect, useRef, useState} from 'react';
import {BaseModal} from 'components/modals';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import {TrendingWordsChartData} from '@/views/storyboard/cellhandlers/TrendingWordsCellHandler';
import {DateTime} from 'luxon';
import {serialize} from '@/views/storyboard/utils/helpers';
import {ProgressBar} from './ProgressBar';
import {ChartFieldToQueryOption} from '@/entities/chart';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import TableComponent from '@/components/table/Table';
import {ColumnDef, ExpandedState} from '@tanstack/react-table';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';

interface Props {
    width: number;
    height: number;
    data: TrendingWordsChartData[];
    chartId: number | null;
    chartQuery: object | any;
    chartTitle: string;
    distinctKeys: string[];
    aggregationField: string | null;
}

export const TableContent = React.memo<Props>(({
    width,
    height,
    data,
    chartId,
    chartTitle,
    chartQuery,
    distinctKeys,
    aggregationField,
}) => {
    const innerContentRef = useRef<HTMLDivElement>(null);
    const [showMoreButtonVisible, setShowMoreButtonVisible] = useState(false);
    const [showMoreModalOpen, setShowMoreModalOpen] = useState(false);
    const offsetHeight = innerContentRef.current?.offsetHeight;
    const {userData, loading: loadingUserData} = useOptimizedConfig();


    useEffect(() => {
        if (offsetHeight && offsetHeight > height) {
            setShowMoreButtonVisible(true);
        } else {
            setShowMoreButtonVisible(false);
        }
    }, [
        offsetHeight,
        data,
        width,
        height,
        setShowMoreButtonVisible,
        loadingUserData,
    ]);

    const keyColumnString = (x: string): string => {
        if (x === 'labelFullComparable') {
            return i18n.t('tc_comparable_date.text');
        }
        if (x === 'docCount') {
            return i18n.t('tc_result.text');
        } else {
            return i18n.t('tc_comparable_result.text');
        }
    };

    const index = distinctKeys.indexOf('words');
    if (index > -1) {
        distinctKeys.splice(index, 1);
    }

    const itemToFind = 'word';
    const foundIdx = distinctKeys.findIndex(el => el === itemToFind);
    distinctKeys.splice(foundIdx, 1);
    distinctKeys.unshift(itemToFind);

    let phraseText: string;
    if (chartQuery?.output === 'disappearing') {
        phraseText = i18n.t('tc_disappearing_words.text');
    } else if (chartQuery?.output === 'trending') {
        phraseText = i18n.t('tc_trending_words.text');
    }

    const trendingTableHeadersRename = (key: string) => {
        key = key.replace('word', phraseText);
        key = key.replace('score', i18n.t('tc_score.text'));
        key = key.replace('count', i18n.t('tc_count.text'));
        return key;
    };

    function trendingWordsFormatter(column: any) {
        if (column.accessorKey === 'count') {
            return (
                <>
                    {column.header}
                    <Tooltip
                        content={i18n.t('jt_total_number.text')}
                        placement="top"
                    >
                        <button
                            type="button"
                            className="btn btn-link p-0 ml-2"
                            style={{cursor: 'pointer', border: 'none', background: 'none'}}
                            data-cy="table-content-btn-1">
                            <Icon name="mdi-information-outline" className='text-info' size='xs' />
                        </button>
                    </Tooltip>
                </>
            );
        }

        if (column.accessorKey === 'score') {
            return (
                <>
                    {column.header}
                    <Tooltip
                        content={i18n.t('jt_hover_over.text')}
                        placement="top"
                    >
                        <button
                            type="button"
                            className="btn btn-link p-0 ml-2"
                            style={{cursor: 'pointer', border: 'none', background: 'none'}}
                            data-cy="table-content-btn">
                            <Icon name="mdi-information-outline" className='text-info' size='xs' />
                        </button>
                    </Tooltip>
                </>
            );
        }

        return (
            <>{column.header}</>
        );
    }

    const parseTrendingDate = (dateStr: string | undefined | null, fallbackSessionEnd: string | undefined | null) => {
        // Primary: try to parse the provided date string (expected format dd/MM/yyyy)
        if (dateStr) {
            const parsed = DateTime.fromFormat(dateStr, 'dd/MM/yyyy');
            if (parsed.isValid) {
                return parsed;
            }
        }

        // Fallback: try session end date from user data (also dd/MM/yyyy)
        if (fallbackSessionEnd) {
            const parsedFallback = DateTime.fromFormat(fallbackSessionEnd, 'dd/MM/yyyy');
            if (parsedFallback.isValid) {
                // For trending charts we want the preceding full day
                return parsedFallback.minus({days: 1});
            }
        }

        // Final fallback: yesterday in system timezone
        return DateTime.now().minus({days: 1});
    };

    const columns: ColumnDef<any, any>[] = [
        {
            id: 'expander',
            header: () => null,
            cell: ({row}) => (
                <div>
                    {row.getCanExpand() ? (
                        <span
                            onClick={row.getToggleExpandedHandler()}
                            style={{
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            {row.getIsExpanded() ? '(-)' : '(+)'}
                        </span>
                    ) : null}
                </div>
            ),
        },
        // @ts-ignore
        ...distinctKeys.map((key) => ({
            id: key,
            accessorKey: key,
            header: trendingWordsFormatter({
                accessorKey: key,
                header: ['docCount', 'comparableDocCount', 'labelFullComparable'].includes(key) ? keyColumnString(key) : trendingTableHeadersRename(key)
            }),
            cell: (cell: any) => {
                if (key === distinctKeys[0]) {
                    const rowWords = cell.row.original.words ? cell.row.original.words.map((x: {
                        word: any;
                    }) => `"${x.word}"`).join(' ') : '';
                    const finalChartQuery: any = {...chartQuery};

                    const trendingPeriodObject = parseTrendingDate(
                        chartQuery.trending_period,
                        userData?.sessionEndDate || ''
                    );
                    let trendingPeriodObjectEnd = trendingPeriodObject;

                    trendingPeriodObjectEnd = trendingPeriodObjectEnd.set({
                        hour: 23,
                        minute: 59,
                        second: 59,
                    });

                    const eventDatetimeString = `event_datetime:[${trendingPeriodObject.toFormat("yyyy-MM-dd'T'HH:mm:ss")} TO ${trendingPeriodObjectEnd.toFormat("yyyy-MM-dd'T'HH:mm:ss")}]`;
                    let fieldToQueryString;

                    if (chartQuery.fields_to_query) {
                        if (chartQuery.fields_to_query === 'all_fields') {
                            fieldToQueryString = `(${chartQuery.query_string}) AND (${rowWords})`;
                        } else {
                            fieldToQueryString = `${chartQuery.fields_to_query}:((${chartQuery.query_string}) AND (${rowWords}))`;
                        }
                    }

                    finalChartQuery.query_string = `${fieldToQueryString} AND ${eventDatetimeString}`;
                    finalChartQuery.fields_to_query = ChartFieldToQueryOption.AllFields;

                    return (
                        <Tooltip
                            content={
                                <div>
                                    {i18n.t('jt_group_semantically.text')}<br />
                                    {cell.getValue()}
                                </div>
                            }
                            placement='top'
                        >
                            <a
                                type='button'
                                className='btn btn-link p-0 ml-2'
                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                href={`${window.URLS.searchView}?${serialize(finalChartQuery)}`}
                                target='_blank' rel="noreferrer"
                            >
                                {`${cell.getValue()} `}
                                <Icon name="mdi-open-in-new" />
                            </a>
                        </Tooltip>
                    );
                } else if (key === distinctKeys[1]) {
                    const value = cell.getValue();
                    if (!value || value.word_sign === '-') {
                        return value ? value.word_sign : null;
                    }

                    const progressPercentage = cell.getValue().word_sign.toFixed(2);
                    const percThisPeriod = cell.getValue().perc_this_period;
                    const percComparablePeriod = cell.getValue().perc_comparable_period;
                    const percChange = cell.getValue().perc_change;

                    return (
                        <ProgressBar
                            progressPercentage={progressPercentage}
                            percThisPeriod={percThisPeriod}
                            percComparablePeriod={percComparablePeriod}
                            percChange={percChange}
                        />
                    );
                } else {
                    return (
                        <>
                            {cell.getValue()}
                        </>
                    );
                }
            },
        })),
    ];
    // @ts-ignore
    const needsExpands = data.filter(x => x.words.length > 1).length > 0;
    if (!needsExpands) {
        columns.splice(columns.findIndex(column => column.id === 'expander'), 1);
    }


    const [expanded, setExpanded] = useState<ExpandedState>({});

    // @ts-ignore
    const renderSubComponent = ({row}: { row: any }) => {
        const words = row.original.words || [];

        return (
            <table className="table table-hover table-bordered g-table w-100">
                <tbody>
                    {words.map((word: any, index: number) => (
                        <tr key={index}>
                            <td>
                                <a
                                    type="button"
                                    className="btn btn-link p-0 ml-2"
                                    style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                    href={`${window.URLS.searchView}?${serialize(createFinalChartQuery(chartQuery, word.word, userData))}`}
                                    target="_blank" rel="noreferrer"
                                >
                                    {`${word.word} `}
                                    <Icon name="mdi-open-in-new" />
                                </a>
                            </td>
                            <td className="pr-0">
                                {word.word_significance === '-' ? (
                                    word.word_significance
                                ) : (
                                    <ProgressBar
                                        progressPercentage={word.word_significance.toFixed(2)}
                                        percThisPeriod={word.perc_this_period}
                                        percComparablePeriod={word.perc_comparable_period}
                                        percChange={word.perc_change}
                                    />
                                )}
                            </td>
                            <td className="pr-0">{word.count_this_period}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const createFinalChartQuery = (chartQuery: any, word: string, userData: any) => {
        const finalChartQuery: any = {...chartQuery};

        const trendingPeriodObject = parseTrendingDate(
            chartQuery.trending_period,
            userData?.sessionEndDate || ''
        );
        let trendingPeriodObjectEnd = trendingPeriodObject;

        trendingPeriodObjectEnd = trendingPeriodObjectEnd.set({
            hour: 23,
            minute: 59,
            second: 59,
        });

        const eventDatetimeString = `event_datetime:[${trendingPeriodObject.toFormat('yyyy-MM-ddTHH:mm:ss')} TO ${trendingPeriodObjectEnd.toFormat('yyyy-MM-ddTHH:mm:ss')}]`;
        let fieldToQueryString;

        if (chartQuery.fields_to_query) {
            if (chartQuery.fields_to_query === 'all_fields') {
                fieldToQueryString = `${chartQuery.query_string} AND "${word}"`;
            } else {
                fieldToQueryString = `${chartQuery.fields_to_query}:(${chartQuery.query_string} AND "${word}")`;
            }
        }

        finalChartQuery.query_string = `${fieldToQueryString} AND ${eventDatetimeString}`;
        finalChartQuery.fields_to_query = ChartFieldToQueryOption.AllFields;

        return finalChartQuery;
    };

    const table = (
        // @ts-ignore
        <TableComponent
            data={data}
            columns={columns}
            state={{expanded}}
            // @ts-ignore
            onExpandedChange={setExpanded}
            // @ts-ignore
            renderSubComponent={renderSubComponent}
        />
    );

    return (
        <div
            className='px-2 comment-wrapper position-relative overflow-hidden'
            style={{
                width,
                height: height - 16,
            }}
        >
            <div className='top-scrollbar-outer'>
                <div className='top-scrollbar-inner' />
            </div>
            <div className='overflow-auto chart-table-container'>
                <div ref={innerContentRef}>
                    {table}
                </div>
            </div>
            {showMoreButtonVisible ? (
                <div className='show-more-button position-absolute w-100 pl-3'>
                    <Button
                        variant="link"
                        size="sm"
                        className='plain-button position-absolute text-info'
                        onClick={() => setShowMoreModalOpen(true)}
                    >
                        {i18n.t('jt_show_all.text')}
                    </Button>
                </div>
            ) : null}

            <BaseModal
                isOpen={showMoreModalOpen}
                onClose={() => setShowMoreModalOpen(false)}
                title={chartTitle}
                size='xl'
                dialogClassName='fixed-height-modal modal-80w'
            >
                <div className='fixed-header-table-wrapper charttype-trendingwords'>
                    {table}
                </div>
            </BaseModal>
        </div>
    );
});
